import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchSuggestion } from '../../../typesense-search.service';

@Component({
  selector: 'app-autocomplete-product-card',
  imports: [CommonModule],
  templateUrl: './autocomplete-product-card.component.html',
  styleUrl: './autocomplete-product-card.component.scss'
})
export class AutocompleteProductCardComponent {
  product = input.required<SearchSuggestion>();

  productClick = output<SearchSuggestion>();
  addToCartClick = output<SearchSuggestion>();

  isLoading = input(false);
  hasSession = input(true);
  active = input(false);

  hasDiscount = computed(() => {
    const p = this.product();
    return p.originalPrice && p.originalPrice > (p.price || 0);
  });

  isSubscriptionOnly = computed(() => {
    const p = this.product();
    return p.isSubscription && !p.isALaCarte;
  });

  isOutOfStock = computed(() => !!this.product().stock?.isOut);
  isLowStock = computed(() => !!this.product().stock?.isLow);
  lowStockText = computed(() => {
    const s = this.product().stock;
    if (!s?.isLow) return null;
    return `Only ${s.available} left`;
  });

  /**
   * Product cut-off (DEV-11470). Resolved against the catalog by the search bar for the
   * suggestions whose indexed rule covers this customer's delivery day — the indexed document
   * on its own cannot know, since it is shared by every customer.
   */
  isCutOffClosed = computed(() => !!this.product().isCutOffClosed);
  /** Its deadline while it is still open, as the backend wrote it. Never built here. */
  cutOffNotice = computed(() => this.product().cutOffNotice || null);

  canQuickAdd = computed(() =>
    this.hasSession() && !!this.product().canQuickAdd && !this.isOutOfStock() && !this.isCutOffClosed()
  );

  hasAttributes = computed(() => !!(this.product().attributes?.length));

  hasMultiplePackages = computed(() => (this.product().packages?.length ?? 0) > 1);

  badgeText = computed(() => {
    const p = this.product();
    if (this.isSubscriptionOnly()) return 'Subscription';
    if (p.variantLabel) return p.variantLabel;
    if (this.hasAttributes() || this.hasMultiplePackages()) return 'Multiple options';
    return null;
  });

  onProductClick(event: Event): void {
    event.preventDefault();
    this.productClick.emit(this.product());
  }

  onAddToCart(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.addToCartClick.emit(this.product());
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    // Detach the handler before swapping src so a broken placeholder can't loop.
    img.onerror = null;
    img.src = 'assets/images/product-placeholder.webp';
  }
}
